import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

// Tags that should be collapsed by default
const DEFAULT_COLLAPSED_TAGS = ["step_status", "context_vars", "equipment", "insurances", "questions_to_ask"];

// Tags that get special styling
const TAG_COLORS = {
  "request-spec": "#2196f3",
  "call-status": "#ff9800",
  "transcript": "#4caf50",
  "summary": "#9c27b0",
  "call-metadata": "#607d8b",
  "call-recording": "#e91e63",
  "call-recording-url": "#e91e63",
  "call-metrics": "#00bcd4"
};

const STYLES = {
  container: {
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
    borderRadius: "4px",
    padding: "12px",
    fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
    fontSize: "12px",
    lineHeight: "1.4",
    overflow: "auto",
    flex: 1,
    minHeight: 0
  },
  toolbar: {
    display: "flex",
    gap: "8px",
    marginBottom: "10px",
    paddingBottom: "8px",
    borderBottom: "1px solid #3c3c3c"
  },
  toolbarBtn: {
    padding: "4px 8px",
    fontSize: "11px",
    backgroundColor: "#3c3c3c",
    color: "#d4d4d4",
    border: "none",
    borderRadius: "3px",
    cursor: "pointer"
  },
  key: {
    color: "#9cdcfe"
  },
  string: {
    color: "#ce9178"
  },
  number: {
    color: "#b5cea8"
  },
  boolean: {
    color: "#569cd6"
  },
  null: {
    color: "#569cd6",
    fontStyle: "italic"
  },
  bracket: {
    color: "#ffd700"
  },
  comma: {
    color: "#d4d4d4"
  },
  collapseBtn: {
    backgroundColor: "transparent",
    border: "none",
    color: "#808080",
    cursor: "pointer",
    padding: "0 4px",
    fontSize: "10px",
    fontFamily: "monospace"
  },
  collapsedPreview: {
    color: "#6a9955",
    fontStyle: "italic",
    marginLeft: "4px"
  },
  tagBadge: {
    padding: "1px 6px",
    borderRadius: "3px",
    fontSize: "10px",
    fontWeight: "bold",
    marginLeft: "8px",
    color: "#fff"
  },
  dataItemHeader: {
    backgroundColor: "#2d2d2d",
    margin: "4px 0",
    padding: "4px 8px",
    borderRadius: "3px",
    borderLeft: "3px solid"
  }
};

// Generate a preview string for collapsed objects/arrays
const getCollapsedPreview = (value, maxLength = 60) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    const itemType = typeof value[0];
    if (itemType === "object" && value[0] !== null) {
      // Check if items have a 'name' or 'tag' property for better preview
      const nameKey = value[0].name ? "name" : value[0].tag ? "tag" : value[0].product_type ? "product_type" : null;
      if (nameKey && value.length <= 5) {
        const names = value.map(v => v[nameKey]).join(", ");
        if (names.length <= maxLength) return `[${names}]`;
      }
    }
    return `Array(${value.length})`;
  }

  if (typeof value === "object" && value !== null) {
    const keys = Object.keys(value);
    if (keys.length === 0) return "{}";
    if (keys.length <= 3) {
      const preview = keys.join(", ");
      if (preview.length <= maxLength) return `{${preview}}`;
    }
    return `Object(${keys.length} keys)`;
  }

  return String(value).substring(0, maxLength);
};

// Check if a key should be collapsed by default
const shouldCollapseByDefault = (key, value) => {
  if (DEFAULT_COLLAPSED_TAGS.includes(key)) return true;
  if (Array.isArray(value) && value.length > 5) return true;
  if (typeof value === "object" && value !== null) {
    const keys = Object.keys(value);
    if (keys.length > 10) return true;
    // Collapse step_status entries that are not_started
    if (value.status === "not_started") return true;
  }
  return false;
};

// Render a single JSON value with syntax highlighting
const JsonValue = ({ value, depth = 0, keyName = null, defaultCollapsed = false, collapsedState, onToggle }) => {
  const path = keyName || "root";
  const isCollapsed = collapsedState[path] ?? defaultCollapsed;

  const toggleCollapse = useCallback(() => {
    onToggle(path, !isCollapsed);
  }, [path, isCollapsed, onToggle]);

  const indent = "  ".repeat(depth);
  const childIndent = "  ".repeat(depth + 1);

  // Handle null
  if (value === null) {
    return <span style={STYLES.null}>null</span>;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span style={STYLES.bracket}>[]</span>;
    }

    if (isCollapsed) {
      return (
        <span>
          <button style={STYLES.collapseBtn} onClick={toggleCollapse} title="Expand">
            [+]
          </button>
          <span style={STYLES.bracket}>[</span>
          <span style={STYLES.collapsedPreview}>{getCollapsedPreview(value)}</span>
          <span style={STYLES.bracket}>]</span>
        </span>
      );
    }

    return (
      <span>
        <button style={STYLES.collapseBtn} onClick={toggleCollapse} title="Collapse">
          [-]
        </button>
        <span style={STYLES.bracket}>[</span>
        {"\n"}
        {value.map((item, index) => (
          <span key={index}>
            {childIndent}
            <JsonValue
              value={item}
              depth={depth + 1}
              keyName={`${path}[${index}]`}
              defaultCollapsed={shouldCollapseByDefault(index, item)}
              collapsedState={collapsedState}
              onToggle={onToggle}
            />
            {index < value.length - 1 && <span style={STYLES.comma}>,</span>}
            {"\n"}
          </span>
        ))}
        {indent}<span style={STYLES.bracket}>]</span>
      </span>
    );
  }

  // Handle objects
  if (typeof value === "object") {
    const keys = Object.keys(value);

    if (keys.length === 0) {
      return <span style={STYLES.bracket}>{"{}"}</span>;
    }

    // Special handling for data_items - show tag badge
    const isDataItem = keys.includes("tag") && keys.includes("data") && keys.includes("group");
    const tagValue = isDataItem ? value.tag : null;
    const tagColor = tagValue ? TAG_COLORS[tagValue] || "#666" : null;

    if (isCollapsed) {
      return (
        <span>
          <button style={STYLES.collapseBtn} onClick={toggleCollapse} title="Expand">
            [+]
          </button>
          <span style={STYLES.bracket}>{"{"}</span>
          <span style={STYLES.collapsedPreview}>{getCollapsedPreview(value)}</span>
          {tagValue && (
            <span style={{ ...STYLES.tagBadge, backgroundColor: tagColor }}>{tagValue}</span>
          )}
          <span style={STYLES.bracket}>{"}"}</span>
        </span>
      );
    }

    return (
      <span>
        <button style={STYLES.collapseBtn} onClick={toggleCollapse} title="Collapse">
          [-]
        </button>
        <span style={STYLES.bracket}>{"{"}</span>
        {tagValue && (
          <span style={{ ...STYLES.tagBadge, backgroundColor: tagColor }}>{tagValue}</span>
        )}
        {"\n"}
        {keys.map((key, index) => {
          const childPath = `${path}.${key}`;
          const childValue = value[key];
          const childDefaultCollapsed = shouldCollapseByDefault(key, childValue);

          return (
            <span key={key}>
              {childIndent}
              <span style={STYLES.key}>&quot;{key}&quot;</span>
              <span style={STYLES.comma}>: </span>
              <JsonValue
                value={childValue}
                depth={depth + 1}
                keyName={childPath}
                defaultCollapsed={childDefaultCollapsed}
                collapsedState={collapsedState}
                onToggle={onToggle}
              />
              {index < keys.length - 1 && <span style={STYLES.comma}>,</span>}
              {"\n"}
            </span>
          );
        })}
        {indent}<span style={STYLES.bracket}>{"}"}</span>
      </span>
    );
  }

  // Handle primitives
  if (typeof value === "string") {
    // Truncate very long strings but allow expansion
    const displayValue = value.length > 500 ? value.substring(0, 500) + "..." : value;
    return <span style={STYLES.string}>&quot;{displayValue}&quot;</span>;
  }

  if (typeof value === "number") {
    return <span style={STYLES.number}>{value}</span>;
  }

  if (typeof value === "boolean") {
    return <span style={STYLES.boolean}>{value.toString()}</span>;
  }

  return <span>{String(value)}</span>;
};

JsonValue.propTypes = {
  value: PropTypes.any,
  depth: PropTypes.number,
  keyName: PropTypes.string,
  defaultCollapsed: PropTypes.bool,
  collapsedState: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired
};

const SynthpopPayloadViewer = ({ payload }) => {
  // Parse payload once
  const parsedPayload = useMemo(() => {
    if (!payload) return null;
    if (typeof payload === "string") {
      try {
        return JSON.parse(payload);
      } catch {
        return null;
      }
    }
    return payload;
  }, [payload]);

  // Build initial collapsed state: data_items array expanded, but each item collapsed
  const initialCollapsedState = useMemo(() => {
    const state = {};
    if (parsedPayload?.data_items) {
      // Keep the array itself expanded so we see the list of items with tag badges
      state["root.data_items"] = false;
      // Collapse each individual item
      parsedPayload.data_items.forEach((_, i) => {
        state[`root.data_items[${i}]`] = true;
      });
    }
    return state;
  }, [parsedPayload]);

  const [collapsedState, setCollapsedState] = useState(initialCollapsedState);

  // Reset to initial state when payload changes
  useEffect(() => {
    setCollapsedState(initialCollapsedState);
  }, [initialCollapsedState]);

  const handleToggle = useCallback((path, collapsed) => {
    setCollapsedState(prev => ({ ...prev, [path]: collapsed }));
  }, []);

  const expandAll = useCallback(() => {
    // Explicitly set all collapsible nodes to expanded (false)
    const newState = {};
    const traverse = (obj, path = "root") => {
      if (Array.isArray(obj)) {
        if (obj.length > 0) newState[path] = false;
        obj.forEach((item, i) => traverse(item, `${path}[${i}]`));
      } else if (typeof obj === "object" && obj !== null) {
        const keys = Object.keys(obj);
        if (keys.length > 0) newState[path] = false;
        keys.forEach(key => traverse(obj[key], `${path}.${key}`));
      }
    };
    if (parsedPayload) {
      traverse(parsedPayload);
      setCollapsedState(newState);
    }
  }, [parsedPayload]);

  const resetView = useCallback(() => {
    // Reset to initial state (data_items collapsed)
    setCollapsedState(initialCollapsedState);
  }, [initialCollapsedState]);

  if (!payload) {
    return <div style={STYLES.container}>No payload data</div>;
  }

  if (!parsedPayload) {
    // Failed to parse - show raw
    return (
      <div style={STYLES.container}>
        <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{payload}</pre>
      </div>
    );
  }

  return (
    <div style={STYLES.container}>
      <div style={STYLES.toolbar}>
        <button style={STYLES.toolbarBtn} onClick={expandAll}>
          Expand All
        </button>
        <button style={STYLES.toolbarBtn} onClick={resetView}>
          Reset View
        </button>
      </div>
      <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        <JsonValue
          value={parsedPayload}
          collapsedState={collapsedState}
          onToggle={handleToggle}
        />
      </pre>
    </div>
  );
};

SynthpopPayloadViewer.propTypes = {
  payload: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
};

export default SynthpopPayloadViewer;
